import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Save, Eye, History, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { WebsiteContent, WebsiteContentVersion, ContentStatus } from '@/types/website';
import { CONTENT_STATUS_LABELS } from '@/types/website';

interface ContentEditorProps {
  content: WebsiteContent;
  versions: WebsiteContentVersion[];
  onSave: (updates: Partial<WebsiteContent> & { createVersion?: boolean; versionNotes?: string }) => void;
  onRestoreVersion?: (version: WebsiteContentVersion) => void;
  isSaving?: boolean;
}

export function ContentEditor({ content, versions, onSave, onRestoreVersion, isSaving }: ContentEditorProps) {
  const [title, setTitle] = useState(content.title);
  const [contentText, setContentText] = useState(content.content);
  const [status, setStatus] = useState<ContentStatus>(content.status as ContentStatus);
  const [showVersions, setShowVersions] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setTitle(content.title);
    setContentText(content.content);
    setStatus(content.status as ContentStatus);
    setHasChanges(false);
  }, [content]);

  useEffect(() => {
    const changed = 
      title !== content.title || 
      contentText !== content.content || 
      status !== content.status;
    setHasChanges(changed);
  }, [title, contentText, status, content]);

  const handleSave = (createVersion = false) => {
    onSave({
      title,
      content: contentText,
      status,
      createVersion,
      versionNotes: createVersion ? `Status: ${CONTENT_STATUS_LABELS[status]}` : undefined,
    });
  };

  const statusColors: Record<string, string> = {
    concept: 'bg-yellow-100 text-yellow-700',
    review: 'bg-blue-100 text-blue-700',
    live: 'bg-green-100 text-green-700',
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Main Editor */}
      <div className="lg:col-span-3 space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <CardTitle className="text-lg">Content bewerken</CardTitle>
                <p className="text-sm text-muted-foreground">/{content.slug}</p>
              </div>
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    Niet opgeslagen
                  </Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVersions(!showVersions)}
                  className="lg:hidden"
                >
                  <History className="h-4 w-4 mr-1" />
                  Versies
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titel</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as ContentStatus)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTENT_STATUS_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            key === 'concept' ? 'bg-yellow-500' :
                            key === 'review' ? 'bg-blue-500' : 'bg-green-500'
                          }`} />
                          {label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content (Markdown)</Label>
              <Textarea
                id="content"
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                placeholder="Schrijf je content hier... Markdown wordt ondersteund."
                className="min-h-[400px] font-mono text-sm"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-muted-foreground">
                {content.updated_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Laatst bewerkt: {format(new Date(content.updated_at), 'd MMM yyyy HH:mm', { locale: nl })}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  disabled={!hasChanges || isSaving}
                >
                  <Save className="h-4 w-4 mr-1" />
                  Opslaan met versie
                </Button>
                <Button
                  onClick={() => handleSave(false)}
                  disabled={!hasChanges || isSaving}
                >
                  <Save className="h-4 w-4 mr-1" />
                  Opslaan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Version History Sidebar */}
      <div className={`lg:block ${showVersions ? 'block' : 'hidden'}`}>
        <Card className="sticky top-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="h-4 w-4" />
              Versiegeschiedenis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {versions.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">
                  Nog geen versies opgeslagen.
                </p>
              ) : (
                <div className="space-y-1 p-2">
                  {versions.map((version, index) => (
                    <div key={version.id}>
                      <div className="p-2 rounded hover:bg-muted/50 cursor-pointer group">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">
                            Versie {versions.length - index}
                          </span>
                          {onRestoreVersion && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs opacity-0 group-hover:opacity-100"
                              onClick={() => onRestoreVersion(version)}
                            >
                              Herstel
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(version.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                        </p>
                        {version.version_notes && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {version.version_notes}
                          </p>
                        )}
                      </div>
                      {index < versions.length - 1 && <Separator />}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
